// FIX-CLIENT-TASK-REPORT (D-359 s.4) - the pure parts of the "דוח משימות ללקוח" dialog: the period presets
// (Israel calendar; a week starts on Sunday), the H:MM duration and the exclusion list.

export type PeriodPreset = 'sinceLast' | 'thisMonth' | 'lastMonth' | 'thisWeek' | 'lastWeek' | 'custom';
export const PERIOD_PRESETS: PeriodPreset[] = ['sinceLast', 'thisMonth', 'lastMonth', 'thisWeek', 'lastWeek', 'custom'];

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
const parse = (s: string) => new Date(`${s}T00:00:00Z`);
export function addDays(day: string, n: number): string {
  const d = parse(day);
  d.setUTCDate(d.getUTCDate() + n);
  return iso(d);
}

/** The period of a preset, `today` = the Israel date (YYYY-MM-DD). `sinceLast` = from the server's default. */
export function presetRange(p: PeriodPreset, today: string, sinceLastFrom?: string | null): { from: string; to: string } | null {
  const d = parse(today);
  switch (p) {
    case 'thisMonth':
      return { from: `${today.slice(0, 7)}-01`, to: today };
    case 'lastMonth': {
      const first = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1));
      const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 0));
      return { from: iso(first), to: iso(last) };
    }
    case 'thisWeek':
      return { from: addDays(today, -d.getUTCDay()), to: today };
    case 'lastWeek': {
      const sunday = addDays(today, -d.getUTCDay() - 7);
      return { from: sunday, to: addDays(sunday, 6) };
    }
    case 'sinceLast':
      return sinceLastFrom ? { from: sinceLastFrom <= today ? sinceLastFrom : today, to: today } : null;
    default:
      return null;
  }
}

/** A valid period: two bare dates, from <= to. */
export function periodError(from: string, to: string): 'missing' | 'order' | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return 'missing';
  return from > to ? 'order' : null;
}

/** Minutes -> "H:MM" (the report's unit). */
export function hm(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  return `${Math.floor(m / 60)}:${pad(m % 60)}`;
}

/** Toggle a task (all its tickets - a merged "צוות מנדי" task has several) in / out of the exclusion list. */
export function toggleExcluded(excluded: string[], ticketIds: string[]): string[] {
  const set = new Set(excluded);
  const allOut = ticketIds.every((id) => set.has(id));
  for (const id of ticketIds) {
    if (allOut) set.delete(id);
    else set.add(id);
  }
  return [...set];
}

/** The e-mail list typed by hand ("a@x.com, b@y.com"): trimmed, de-duplicated, invalid ones returned apart. */
export function parseEmails(text: string): { valid: string[]; invalid: string[] } {
  const valid: string[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();
  for (const raw of text.split(/[,;\s]+/)) {
    const a = raw.trim();
    if (!a) continue;
    if (!/^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/.test(a)) { invalid.push(a); continue; }
    if (seen.has(a.toLowerCase())) continue;
    seen.add(a.toLowerCase());
    valid.push(a);
  }
  return { valid, invalid };
}

/**
 * D-359 s.6 (the screen's mirror of the server's check): the OTE manager, the customer's account manager, or an
 * employee to whom "פירוט לוגים" was opened (D-335 override / D-340 "all employees"). `orgId` null = any customer
 * (the "פירוט לוגים" picker: an account manager of at least one). A customer login never.
 */
export function mayProduceClientReport(
  caps: { isGeneralManager: boolean; managedOrgIds: string[] },
  orgId: string | null | undefined,
  logDetailOpened: boolean
): boolean {
  if (caps.isGeneralManager || logDetailOpened) return true;
  return orgId ? caps.managedOrgIds.includes(orgId) : caps.managedOrgIds.length > 0;
}
